import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import ProjectJiraItems from './ProjectJiraItems';
import axiosInstance from '../../api/axiosInstance';
import { useWorkspace } from '../../context/WorkspaceContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';

const JiraItemsPage = ({ embeddedIdAndName } = {}) => {
  const params = useParams();
  const idAndName = embeddedIdAndName || params.idAndName;
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const isEmbedded = Boolean(embeddedIdAndName);

  const [id, ...nameParts] = (idAndName || '').split('-');
  const name = nameParts.join('-');
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        setProject({
          id: parseInt(id, 10),
          name: name.replace(/-/g, ' '),
        });

        const response = await axiosInstance.get(`/projects/get_project?project_id=${id}`);
        if (response?.data) {
          setProject({
            id: parseInt(id, 10),
            name: response.data.project_name || name.replace(/-/g, ' '),
            description: response.data.description,
            type: response.data.project_type,
            status: response.data.status,
          });
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id && name) {
      fetchProjectDetails();
    }
  }, [id, name]);

  const handleBack = () => {
    const workspaceId = getWorkspaceIdFromURL() || currentWorkspace?.id;
    const projectId = getProjectIdFromURL() || project?.id;

    if (!workspaceId) {
      navigate('/dashboard/all-workspaces');
      return;
    }

    // Keep project context when leaving Jira — never dump to bare workspace projects
    if (projectId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, projectId));
      return;
    }

    navigate(buildWorkspaceDashboardPath(workspaceId, null, 'projects'));
  };

  if (loading && !project) {
    return (
      <div className="loading-spinner">
        <i className="fas fa-spinner fa-spin"></i>
      </div>
    );
  }

  if (!project) {
    return <div>Error: No project information available</div>;
  }

  return (
    <ProjectJiraItems
      project={project}
      onClose={isEmbedded ? undefined : handleBack}
      hideBack={isEmbedded}
      onNavigateToTestSuite={(testSuiteId) => {
        const workspaceId = getWorkspaceIdFromURL() || currentWorkspace?.id;
        const projectId = getProjectIdFromURL() || project?.id;
        if (workspaceId && projectId) {
          navigate(
            buildWorkspaceDashboardPath(workspaceId, projectId, `test-suites/${testSuiteId}`)
          );
        } else {
          navigate(`/test-suites/${testSuiteId}`);
        }
      }}
    />
  );
};

export default JiraItemsPage;
